"use client";
/**
 * No media at all: the figures and what they are measured against.
 *
 * This is the composition for comparing two edible things rather than
 * recognising one, so everything on it is a number with its unit and its basis.
 * The micronutrient panel is here in full — including the sentence naming which
 * unit system it is in — because that is the one fact a reader of this pillar
 * can act on wrongly, and a board is where somebody reads figures closely.
 */
import type { ReactElement } from "react";
import {
  CresultcardIdentity,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardAlign,
  CresultcardDensity,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardStackDensity,
  CresultcardTilePadding,
  CresultcardTitleLines,
} from "@/components/ui/Cresultcard";
import {
  C_NUTRITION_CARD_MODIFIER,
} from "./cnutritioncard.constants";
import {
  cnutritioncardAlternateServingCount,
  cnutritioncardLeadServing,
} from "./cnutritioncard.model";
import {
  CnutritioncardEnergy,
  CnutritioncardMetaLine,
  CnutritioncardServingCount,
} from "./CnutritioncardMeta";
import { CnutritioncardNutrientPanel } from "./CnutritioncardNutrientPanel";
import { CnutritioncardShell } from "./CnutritioncardShell";
import {
  CnutritioncardVariant,
  type CnutritioncardVariantProps,
} from "./cnutritioncard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

export function CnutritioncardMacroBoard({
  data,
  servingScope,
  openControl,
}: CnutritioncardVariantProps): ReactElement {
  const serving = cnutritioncardLeadServing(data);

  return (
    <CnutritioncardShell
      density={CresultcardDensity.Card}
      head={
        <div
          style={{ minWidth: 0, padding: CARD_INSET }}
        >
          <CresultcardIdentity
            openControl={openControl}
            secondary={
              <CnutritioncardMetaLine
                data={data}
              />
            }
            title={data.name}
            titleLines={CresultcardTitleLines.Two}
            truncationOrder={CARD_TRUNCATION}
          />
        </div>
      }
      modifier={C_NUTRITION_CARD_MODIFIER[CnutritioncardVariant.MacroBoard]}
      openable
      variant={CnutritioncardVariant.MacroBoard}
      body={
        <div
          style={{
            minWidth: 0,
            paddingInline: CARD_INSET,
            paddingBlockEnd: CARD_INSET,
          }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Regular}
          >
            {/* The energy figure sits in the BODY, not in the identity's
                trailing slot, and the reason is the scope line under it.
                The basis can be the vendor's own
                `1 oz, with bone cooked (yield after bone removed)` — 53
                characters with its prefix — and a trailing slot beside a
                title is the one place on a card that cannot absorb that
                without taking the width from the name. In the body it gets
                the card's whole inner measure. `Cshoppingcard` puts its
                scope-bearing price in exactly the same region. */}
            <CnutritioncardEnergy
              align={CresultcardAlign.Start}
              data={data}
              scale={CresultcardFigureScale.Card}
              servingScope={servingScope}
            />
            {serving != null && (
              <CnutritioncardNutrientPanel
                serving={serving}
              />
            )}
            <CnutritioncardServingCount
              count={cnutritioncardAlternateServingCount(data)}
            />
          </CresultcardStack>
        </div>
      }
    />
  );
}
